import * as THREE from 'three';
import {
  ParametresTraverses,
  StructureTraverses,
  RangeeTraverses,
  GeometrieTraverse,
  TypeMotifTraverse,
  TypeMateriauTraverse,
  Point3D,
  Point2D,
  DonneesStructureBase,
  MotifPersonnalise,
  CourbeBezier
} from '../types';

/**
 * Service de génération 3D des traverses
 * Responsable de la création de la géométrie 3D des traverses selon les motifs choisis
 * Conforme au cahier des charges - Module 2: Les Traverses
 */
export class Traverses3DService {
  private materiau: THREE.Material;

  constructor() {
    this.materiau = new THREE.MeshLambertMaterial({ 
      color: 0x8B4513, // Couleur bois par défaut
      transparent: false
    });
  }

  /**
   * Génère la structure complète des traverses
   * Tâche 2.1: Fonctions de Génération de Traverses
   */
  public genererTraverses(
    parametres: ParametresTraverses,
    donneesBase: DonneesStructureBase
  ): StructureTraverses {
    if (!parametres.actif) {
      return this.creerStructureVide(parametres);
    }

    // Mettre à jour le matériau selon le type et la couleur
    const couleurNumerique = typeof parametres.couleur === 'string' 
      ? parseInt(parametres.couleur.replace('#', ''), 16) 
      : parametres.couleur;
    this.mettreAJourMateriau(couleurNumerique);

    const rangees: RangeeTraverses[] = [];
    
    // Générer chaque rangée de traverses
    // Tâche 2.3: Interface des Paramètres de Traverses - rangées multiples
    for (let i = 0; i < parametres.nombreRangees; i++) {
      const hauteur = this.calculerHauteurRangee(i, parametres, donneesBase);
      const rangee = this.genererRangeeTraverses(
        i,
        hauteur,
        parametres,
        donneesBase
      );
      rangees.push(rangee);
    }



    return {
      id: 'structure_traverses',
      parametres,
      rangees
    };
  }

  /**
   * Génère la géométrie 3D pour Three.js
   * Méthode appelée par le composant Visualisateur3D
   */
  public genererTraverses3D(structure: StructureTraverses): THREE.Group {
    const groupe = new THREE.Group();
    groupe.name = 'traverses';
    let totalTraverses = 0;
    let traversesAjoutees = 0;

    structure.rangees.forEach((rangee, indexRangee) => {
      rangee.traverses.forEach((traverse, indexTraverse) => {
        totalTraverses++;
        
        if (traverse.points.length > 0 && traverse.indices.length > 0) {
          const mesh = this.creerMeshTraverse(traverse);
          if (mesh) {
            groupe.add(mesh);
            traversesAjoutees++;
          }
        }
      });
    });
    
    return groupe;
  }

  /**
   * Calcule la hauteur d'une rangée de traverses
   * Tâche 2.3: Ajuster la hauteur verticale des traverses
   */
  private calculerHauteurRangee(
    indexRangee: number,
    parametres: ParametresTraverses,
    donneesBase: DonneesStructureBase
  ): number {
    const hauteurBase = donneesBase.hauteur * parametres.hauteurPosition;
    const espacementTotal = (parametres.nombreRangees - 1) * parametres.espacementVertical;
    const hauteurDebut = hauteurBase - espacementTotal / 2;
    
    return hauteurDebut + (indexRangee * parametres.espacementVertical);
  }

  /**
   * Génère une rangée de traverses selon le motif choisi
   * Tâche 2.2: Bibliothèque de Motifs Prédéfinis
   */
  private genererRangeeTraverses(
    indexRangee: number,
    hauteur: number,
    parametres: ParametresTraverses,
    donneesBase: DonneesStructureBase
  ): RangeeTraverses {
    let traverses: GeometrieTraverse[] = [];

    // Sélection du motif selon le cahier des charges
    switch (parametres.motif) {
      case TypeMotifTraverse.SIMPLE:
        traverses = this.genererMotifSimple(hauteur, parametres, donneesBase);
        break;
      case TypeMotifTraverse.VAGUE_DOUCE:
        traverses = this.genererMotifVague(hauteur, parametres, donneesBase);
        break;
      case TypeMotifTraverse.LOSANGE:
        traverses = this.genererMotifLosange(hauteur, parametres, donneesBase);
        break;
      case TypeMotifTraverse.PERSONNALISE:
        traverses = this.genererMotifPersonnalise(hauteur, parametres, donneesBase);
        break;
      default:
        traverses = this.genererMotifSimple(hauteur, parametres, donneesBase);
    }

    return {
      id: `rangee_${indexRangee}`,
      hauteur,
      traverses,
      motifApplique: parametres.motif
    };
  }

  /**
   * Génère le motif simple (traverses entre montants adjacents)
   * Tâche 2.2: Motif prédéfini simple
   */
  private genererMotifSimple(
    hauteur: number,
    parametres: ParametresTraverses,
    donneesBase: DonneesStructureBase
  ): GeometrieTraverse[] {
    const traverses: GeometrieTraverse[] = [];
    const positionsMontants = donneesBase.positionsMontants;
    
    if (!positionsMontants || positionsMontants.length < 2) {
      console.warn('Pas assez de montants pour générer des traverses simples');
      return traverses;
    }

    // Déterminer le type de forme
    const estCirculaire = donneesBase.forme === 'cercle';
    const estOvale = donneesBase.forme === 'ovale';
    const estPolygone = donneesBase.forme === 'polygone';
    const estCourbe = estCirculaire || estOvale;
    
    // Calculer le centre et le rayon pour les formes courbes
    const centre = (estCourbe || estPolygone) ? this.calculerCentre(positionsMontants) : null;
    const rayon = estCirculaire && centre ? this.calculerRayon(centre, positionsMontants[0]) : 0;

    // Créer des traverses entre montants adjacents
    for (let i = 0; i < positionsMontants.length; i++) {
      const montantActuel = positionsMontants[i];
      const montantSuivant = positionsMontants[(i + 1) % positionsMontants.length];
      
      if (estCirculaire && centre && rayon > 0) {
        // Générer des points suivant l'arc circulaire
        const pointsArc = this.genererPointsArc(montantActuel, montantSuivant, centre, rayon, hauteur);
        const segments = this.appliquerExclusionsPortesSurPolyline(pointsArc, hauteur, donneesBase);
        segments.forEach((segPts, si) => {
          if (segPts.length < 2) return;
          const traverse = this.creerTraverseCourbe(segPts, parametres.epaisseur, parametres.materiau);
          traverses.push({
            id: `traverse_simple_${i}_${si}`,
            points: traverse.points,
            indices: traverse.indices,
            normales: traverse.normales,
            uv: traverse.uv
          });
        });
      } else if (estOvale && centre) {
        // Générer des points suivant l'arc elliptique
        const pointsArc = this.genererPointsArcElliptique(montantActuel, montantSuivant, centre, donneesBase, hauteur);
        const segments = this.appliquerExclusionsPortesSurPolyline(pointsArc, hauteur, donneesBase);
        segments.forEach((segPts, si) => {
          if (segPts.length < 2) return;
          const traverse = this.creerTraverseCourbe(segPts, parametres.epaisseur, parametres.materiau);
          traverses.push({
            id: `traverse_simple_${i}_${si}`,
            points: traverse.points,
            indices: traverse.indices,
            normales: traverse.normales,
            uv: traverse.uv
          });
        });
      } else if (estPolygone && centre) {
        // Générer des points suivant le périmètre du polygone
        const pointsPolygone = this.genererPointsPolygone(montantActuel, montantSuivant, centre, donneesBase, hauteur);
        const segments = this.appliquerExclusionsPortesSurPolyline(pointsPolygone, hauteur, donneesBase);
        segments.forEach((segPts, si) => {
          if (segPts.length < 2) return;
          const traverse = this.creerTraverseCourbe(segPts, parametres.epaisseur, parametres.materiau);
          traverses.push({
            id: `traverse_simple_${i}_${si}`,
            points: traverse.points,
            indices: traverse.indices,
            normales: traverse.normales,
            uv: traverse.uv
          });
        });
      } else {
        // Créer une traverse droite pour les formes rectangulaires
        // Échantillonner une polyligne pour permettre la découpe par les portes
        const nombreSegments = 20;
        const linePoints: Point3D[] = [];
        for (let k = 0; k <= nombreSegments; k++) {
          const t = k / nombreSegments;
          linePoints.push({
            x: montantActuel.x + (montantSuivant.x - montantActuel.x) * t,
            y: hauteur,
            z: montantActuel.z + (montantSuivant.z - montantActuel.z) * t
          });
        }
        const segments = this.appliquerExclusionsPortesSurPolyline(linePoints, hauteur, donneesBase);
        segments.forEach((segPts, si) => {
          if (segPts.length < 2) return;
          const a = segPts[0];
          const b = segPts[segPts.length - 1];
          const traverse = this.creerTraverseDroite(a, b, hauteur, parametres.epaisseur, parametres.materiau);
          traverses.push({
            id: `traverse_simple_${i}_${si}`,
            points: traverse.points,
            indices: traverse.indices,
            normales: traverse.normales,
            uv: traverse.uv
          });
        });
      }
    }

    return traverses;
  }

  /**
   * Calcule les points de connexion des traverses en tenant compte de l'épaisseur des montants
   */
  private calculerPointsConnexion(
    montantA: Point3D, 
    montantB: Point3D, 
    epaisseurMontant: number
  ): { pointDebut: Point3D; pointFin: Point3D } {
    // Calculer la direction entre les montants
    const dx = montantB.x - montantA.x;
    const dz = montantB.z - montantA.z;
    const distance = Math.sqrt(dx * dx + dz * dz);
    
    if (distance === 0) {
      return {
        pointDebut: { ...montantA },
        pointFin: { ...montantB }
      };
    }
    
    // Vecteur unitaire dans la direction A vers B
    const ux = dx / distance;
    const uz = dz / distance;
    
    // Rayon du montant (épaisseur / 2)
    const rayonMontant = epaisseurMontant / 2; // L'épaisseur est déjà en unités appropriées
    
    // Points de connexion sur les bords des montants
    const pointDebut: Point3D = {
      x: montantA.x + ux * rayonMontant,
      y: montantA.y,
      z: montantA.z + uz * rayonMontant
    };
    
    const pointFin: Point3D = {
      x: montantB.x - ux * rayonMontant,
      y: montantB.y,
      z: montantB.z - uz * rayonMontant
    };
    
    return { pointDebut, pointFin };
  }

  /**
   * Calcule le centre d'un ensemble de points (pour les structures circulaires)
   */
  private calculerCentre(positions: Point3D[]): Point3D {
    let sumX = 0, sumZ = 0;
    for (const pos of positions) {
      sumX += pos.x;
      sumZ += pos.z;
    }
    return {
      x: sumX / positions.length,
      y: 0,
      z: sumZ / positions.length
    };
  }

  /**
   * Calcule le rayon d'un cercle à partir du centre et d'un point
   */
  private calculerRayon(centre: Point3D, point: Point3D): number {
    return Math.sqrt(
      Math.pow(point.x - centre.x, 2) + Math.pow(point.z - centre.z, 2)
    );
  }

  /**
   * Génère les points d'un arc de cercle entre deux montants
   */
  private genererPointsArc(montantA: Point3D, montantB: Point3D, centre: Point3D, rayon: number, hauteur: number): Point3D[] {
    const points: Point3D[] = [];
    
    // Calculer les angles des deux montants par rapport au centre
    const angleA = Math.atan2(montantA.z - centre.z, montantA.x - centre.x);
    const angleB = Math.atan2(montantB.z - centre.z, montantB.x - centre.x);
    
    // Déterminer l'angle de différence en prenant le chemin le plus court
    let angleDiff = angleB - angleA;
    if (angleDiff > Math.PI) angleDiff -= 2 * Math.PI;
    if (angleDiff < -Math.PI) angleDiff += 2 * Math.PI;
    
    // Nombre de segments pour l'arc (plus il y en a, plus l'arc est lisse)
    const nombreSegments = Math.max(20, Math.abs(angleDiff) * 20); // Augmenté pour plus de lissage
    
    // Ajouter le point de départ (position exacte du montant A)
    points.push({ x: montantA.x, y: hauteur, z: montantA.z });
    
    // Générer les points intermédiaires de l'arc
    for (let i = 1; i < nombreSegments; i++) {
      const t = i / nombreSegments;
      const angle = angleA + angleDiff * t;
      
      const x = centre.x + rayon * Math.cos(angle);
      const z = centre.z + rayon * Math.sin(angle);
      
      points.push({ x, y: hauteur, z });
    }
    
    // Ajouter le point d'arrivée (position exacte du montant B)
    points.push({ x: montantB.x, y: hauteur, z: montantB.z });
    
    return points;
  }

  /**
   * Génère les points d'un arc elliptique entre deux montants pour les ovales
   */
  private genererPointsArcElliptique(montantA: Point3D, montantB: Point3D, centre: Point3D, donneesBase: DonneesStructureBase, hauteur: number): Point3D[] {
    const points: Point3D[] = [];
    
    // Pour les ovales, utiliser les dimensions de la structure
    const a = donneesBase.largeur / 2; // demi-largeur (axe X)
    const b = donneesBase.longueur / 2; // demi-longueur (axe Z)
    
    // Fonction pour calculer le paramètre t sur l'ellipse à partir d'un point
    const calculerParametreEllipse = (point: Point3D): number => {
      const dx = point.x - centre.x;
      const dz = point.z - centre.z;
      return Math.atan2(dz / b, dx / a);
    };
    
    // Calculer les paramètres t pour les deux montants
    const tA = calculerParametreEllipse(montantA);
    const tB = calculerParametreEllipse(montantB);
    
    // Déterminer la différence de paramètre en prenant le chemin le plus court
    let tDiff = tB - tA;
    if (tDiff > Math.PI) tDiff -= 2 * Math.PI;
    if (tDiff < -Math.PI) tDiff += 2 * Math.PI;
    
    // Nombre de segments pour l'arc elliptique
    const nombreSegments = Math.max(20, Math.abs(tDiff) * 20); // Augmenté pour plus de lissage
    
    // Ajouter le point de départ (position exacte du montant A)
    points.push({ x: montantA.x, y: hauteur, z: montantA.z });
    
    // Générer les points intermédiaires de l'arc elliptique
    for (let i = 1; i < nombreSegments; i++) {
      const ratio = i / nombreSegments;
      const t = tA + tDiff * ratio;
      
      // Formule de l'ellipse paramétrée : x = a*cos(t), z = b*sin(t)
      const x = centre.x + a * Math.cos(t);
      const z = centre.z + b * Math.sin(t);
      
      points.push({ x, y: hauteur, z });
    }
    
    // Ajouter le point d'arrivée (position exacte du montant B)
    points.push({ x: montantB.x, y: hauteur, z: montantB.z });
    
    return points;
  }

  /**
   * Génère des points suivant le périmètre d'un polygone entre deux montants
   */
  private genererPointsPolygone(montantA: Point3D, montantB: Point3D, centre: Point3D, donneesBase: DonneesStructureBase, hauteur: number): Point3D[] {
    const points: Point3D[] = [];
    
    // Pour les polygones, les montants sont déjà positionnés sur les sommets
    // La traverse doit suivre le côté du polygone (ligne droite entre les montants)
    // mais avec suffisamment de points pour une géométrie lisse
    
    const nombreSegments = 20; // Nombre de segments pour lisser la traverse (augmenté)
    
    // Ajouter le point de départ (position exacte du montant A)
    points.push({ x: montantA.x, y: hauteur, z: montantA.z });
    
    // Générer les points intermédiaires en ligne droite
    for (let i = 1; i < nombreSegments; i++) {
      const t = i / nombreSegments;
      const x = montantA.x + (montantB.x - montantA.x) * t;
      const z = montantA.z + (montantB.z - montantA.z) * t;
      
      points.push({ x, y: hauteur, z });
    }
    
    // Ajouter le point d'arrivée (position exacte du montant B)
    points.push({ x: montantB.x, y: hauteur, z: montantB.z });
    
    return points;
  }

  /**
   * Trouve l'index du point le plus proche d'une position donnée
   */
  private trouverPointLePlusProche(position: Point3D, points: Point3D[]): number {
    let indexMin = 0;
    let distanceMin = Number.MAX_VALUE;
    
    for (let i = 0; i < points.length; i++) {
      const point = points[i];
      const distance = Math.sqrt(
        Math.pow(point.x - position.x, 2) + 
        Math.pow(point.z - position.z, 2)
      );
      
      if (distance < distanceMin) {
        distanceMin = distance;
        indexMin = i;
      }
    }
    
    return indexMin;
  }

  /**
   * Extrait les points du périmètre entre deux indices
   */
  private extrairePointsPerimetre(indexDebut: number, indexFin: number, pointsPoutre: Point3D[], hauteur: number): Point3D[] {
    const pointsSegment: Point3D[] = [];
    const totalPoints = pointsPoutre.length;
    
    // Gérer le cas où on traverse la fin du tableau (périmètre fermé)
    if (indexFin < indexDebut) {
      // Aller de indexDebut à la fin
      for (let i = indexDebut; i < totalPoints - 1; i++) {
        pointsSegment.push({
          x: pointsPoutre[i].x,
          y: hauteur,
          z: pointsPoutre[i].z
        });
      }
      // Puis du début à indexFin
      for (let i = 0; i <= indexFin; i++) {
        pointsSegment.push({
          x: pointsPoutre[i].x,
          y: hauteur,
          z: pointsPoutre[i].z
        });
      }
    } else {
      // Cas normal : aller de indexDebut à indexFin
      for (let i = indexDebut; i <= indexFin; i++) {
        pointsSegment.push({
          x: pointsPoutre[i].x,
          y: hauteur,
          z: pointsPoutre[i].z
        });
      }
    }
    
    return pointsSegment;
  }

  /**
   * Extrait les points courbes pour les côtés du losange
   */
  private extrairePointsCourbeLosange(
    indexDebut: number, 
    indexFin: number, 
    pointsSegment: Point3D[], 
    hauteurDebut: number, 
    hauteurFin: number
  ): Point3D[] {
    const points: Point3D[] = [];
    const nombrePointsIntermediaires = 5; // Nombre de points pour créer une courbe lisse
    
    // Calculer les indices réels en tenant compte de la direction
    let indices: number[] = [];
    
    if (indexDebut <= indexFin) {
      // Progression normale
      for (let i = indexDebut; i <= indexFin; i++) {
        indices.push(i);
      }
    } else {
      // Progression inverse (pour fermer le losange)
      for (let i = indexDebut; i >= indexFin; i--) {
        indices.push(i);
      }
    }
    
    // Si on n'a que 2 points, ajouter des points intermédiaires
    if (indices.length < nombrePointsIntermediaires) {
      const pointDebut = pointsSegment[indexDebut];
      const pointFin = pointsSegment[indexFin];
      
      for (let i = 0; i < nombrePointsIntermediaires; i++) {
        const t = i / (nombrePointsIntermediaires - 1);
        const hauteurInterpolée = hauteurDebut + (hauteurFin - hauteurDebut) * t;
        
        points.push({
          x: pointDebut.x + (pointFin.x - pointDebut.x) * t,
          y: hauteurInterpolée,
          z: pointDebut.z + (pointFin.z - pointDebut.z) * t
        });
      }
    } else {
      // Utiliser les points du périmètre avec interpolation de hauteur
      for (let i = 0; i < indices.length; i++) {
        const index = indices[i];
        const t = i / (indices.length - 1);
        const hauteurInterpolée = hauteurDebut + (hauteurFin - hauteurDebut) * t;
        
        const point = pointsSegment[index];
        points.push({
          x: point.x,
          y: hauteurInterpolée,
          z: point.z
        });
      }
    }
    
    return points;
  }

  /**
   * Génère le motif vague douce
   * Tâche 2.2: Motif prédéfini vague douce
   */
  private genererMotifVague(
    hauteur: number,
    parametres: ParametresTraverses,
    donneesBase: DonneesStructureBase
  ): GeometrieTraverse[] {
    const traverses: GeometrieTraverse[] = [];
    const positionsMontants = donneesBase.positionsMontants;
    
    if (!positionsMontants || positionsMontants.length < 2) {
      console.warn('Pas assez de montants pour générer des traverses vague');
      return traverses;
    }

    // Amplitude de la vague fixe, indépendante de l'épaisseur
    const amplitudeVague = 0.1; // 10cm d'amplitude fixe

    // Déterminer le type de forme
    const estCirculaire = donneesBase.forme === 'cercle';
    const estOvale = donneesBase.forme === 'ovale';
    const estPolygone = donneesBase.forme === 'polygone';
    const estCourbe = estCirculaire || estOvale;
    
    // Calculer le centre et le rayon pour les formes courbes
    const centre = (estCourbe || estPolygone) ? this.calculerCentre(positionsMontants) : null;
    const rayon = estCirculaire && centre ? this.calculerRayon(centre, positionsMontants[0]) : 0;

    // Créer des traverses ondulées entre montants adjacents
    for (let i = 0; i < positionsMontants.length; i++) {
      const montantActuel = positionsMontants[i];
      const montantSuivant = positionsMontants[(i + 1) % positionsMontants.length];
      
      let pointsBase: Point3D[];
      
      if (estCirculaire && centre && rayon > 0) {
        // Générer les points d'arc pour une structure circulaire
        pointsBase = this.genererPointsArc(montantActuel, montantSuivant, centre, rayon, hauteur);
      } else if (estOvale && centre) {
        // Générer les points d'arc elliptique pour une structure ovale
        pointsBase = this.genererPointsArcElliptique(montantActuel, montantSuivant, centre, donneesBase, hauteur);
      } else if (estPolygone && centre) {
        // Générer les points suivant le périmètre du polygone
        pointsBase = this.genererPointsPolygone(montantActuel, montantSuivant, centre, donneesBase, hauteur);
      } else {
        // Générer des points en ligne droite pour les autres formes
        const nombrePoints = 20;
        pointsBase = [];
        for (let j = 0; j <= nombrePoints; j++) {
          const t = j / nombrePoints;
          pointsBase.push({
            x: montantActuel.x + (montantSuivant.x - montantActuel.x) * t,
            y: hauteur,
            z: montantActuel.z + (montantSuivant.z - montantActuel.z) * t
          });
        }
      }
      
      // Appliquer l'effet vague aux points de base
      const pointsVague: Point3D[] = pointsBase.map((point, index) => {
        const t = index / (pointsBase.length - 1);
        // 2 ondulations entre chaque paire de montants adjacents
        const nombreCycles = 2;
        const offsetVague = Math.sin(t * Math.PI * nombreCycles) * amplitudeVague;
        
        return {
          x: point.x,
          y: point.y + offsetVague,
          z: point.z
        };
      });

      const traverse = this.creerTraverseCourbe(pointsVague, parametres.epaisseur, parametres.materiau);

      traverses.push({
        id: `traverse_vague_${i}`,
        points: traverse.points,
        indices: traverse.indices,
        normales: traverse.normales,
        uv: traverse.uv
      });
    }

    return traverses;
  }

  /**
   * Génère le motif losange
   * Tâche 2.2: Motif prédéfini losange
   * Tâche 2.4: Adaptation de Motif - s'ajuste au nombre de montants
   */
  private genererMotifLosange(
    hauteur: number,
    parametres: ParametresTraverses,
    donneesBase: DonneesStructureBase
  ): GeometrieTraverse[] {
    const traverses: GeometrieTraverse[] = [];
    const positionsMontants = donneesBase.positionsMontants;
    
    if (!positionsMontants || positionsMontants.length < 2) {
      console.warn('Pas assez de montants pour générer des losanges');
      return this.genererMotifSimple(hauteur, parametres, donneesBase);
    }

    // Hauteur des losanges adaptée à l'espacement vertical
    // S'assurer qu'elle est suffisante pour créer un losange visible
    const hauteurLosange = Math.max(parametres.espacementVertical || 0.4, 0.3);
    
    // 2 losanges entre chaque paire de montants adjacents
    const nombreLosanges = 2;

    // Déterminer le type de forme
    const estCirculaire = donneesBase.forme === 'cercle';
    const estOvale = donneesBase.forme === 'ovale';
    const estPolygone = donneesBase.forme === 'polygone';
    const estCourbe = estCirculaire || estOvale;
    
    // Calculer le centre et le rayon pour les formes courbes
    const centre = (estCourbe || estPolygone) ? this.calculerCentre(positionsMontants) : null;
    const rayon = estCirculaire && centre ? this.calculerRayon(centre, positionsMontants[0]) : 0;

    // Créer des losanges entre chaque paire de montants adjacents
    for (let i = 0; i < positionsMontants.length; i++) {
      const montantA = positionsMontants[i];
      const montantB = positionsMontants[(i + 1) % positionsMontants.length];
      
      let pointsSegment: Point3D[];
      
      if (estCirculaire && centre && rayon > 0) {
        // Générer les points d'arc pour une structure circulaire
        pointsSegment = this.genererPointsArc(montantA, montantB, centre, rayon, hauteur);
      } else if (estOvale && centre) {
        // Générer les points d'arc elliptique pour une structure ovale
        pointsSegment = this.genererPointsArcElliptique(montantA, montantB, centre, donneesBase, hauteur);
      } else if (estPolygone && centre) {
        // Générer les points suivant le périmètre du polygone
        pointsSegment = this.genererPointsPolygone(montantA, montantB, centre, donneesBase, hauteur);
      } else {
        // Utiliser directement les positions des montants pour les formes rectangulaires
        pointsSegment = [
          { x: montantA.x, y: hauteur, z: montantA.z },
          { x: montantB.x, y: hauteur, z: montantB.z }
        ];
      }
      
      const debutSegment = pointsSegment[0];
      const finSegment = pointsSegment[pointsSegment.length - 1];
      
      // Distance entre les points de début et fin du segment
      const distance = Math.sqrt(
        Math.pow(finSegment.x - debutSegment.x, 2) + 
        Math.pow(finSegment.z - debutSegment.z, 2)
      );
      
      // Largeur de chaque losange
      const largeurLosange = distance / nombreLosanges;
      
      // Créer les losanges
      for (let j = 0; j < nombreLosanges; j++) {
        const debutLosange = j / nombreLosanges;
        const finLosange = (j + 1) / nombreLosanges;
        const milieuLosange = (debutLosange + finLosange) / 2;
        
        let pointDebut, pointMilieuHaut, pointFin, pointMilieuBas;
        
        if (estCourbe || estPolygone) {
          // Pour les formes courbes et polygonales, créer des segments courbes pour chaque côté du losange
          const indexDebut = Math.floor(debutLosange * (pointsSegment.length - 1));
          const indexFin = Math.floor(finLosange * (pointsSegment.length - 1));
          const indexMilieu = Math.floor(milieuLosange * (pointsSegment.length - 1));
          
          pointDebut = {
            x: pointsSegment[indexDebut].x,
            y: hauteur,
            z: pointsSegment[indexDebut].z
          };
          
          pointMilieuHaut = {
            x: pointsSegment[indexMilieu].x,
            y: hauteur + hauteurLosange,
            z: pointsSegment[indexMilieu].z
          };
          
          pointFin = {
            x: pointsSegment[indexFin].x,
            y: hauteur,
            z: pointsSegment[indexFin].z
          };
          
          pointMilieuBas = {
            x: pointsSegment[indexMilieu].x,
            y: hauteur - hauteurLosange,
            z: pointsSegment[indexMilieu].z
          };
          
          // Créer des segments courbes pour chaque côté du losange
          
          // Côté montant (gauche-haut) - suit la courbure
          const pointsCourbe1 = this.extrairePointsCourbeLosange(indexDebut, indexMilieu, pointsSegment, hauteur, hauteur + hauteurLosange);
          const traverse1 = this.creerTraverseCourbe(pointsCourbe1, parametres.epaisseur, parametres.materiau);
          traverses.push({
            id: `traverse_losange_${i}_${j}_1`,
            points: traverse1.points,
            indices: traverse1.indices,
            normales: traverse1.normales,
            uv: traverse1.uv
          });
          
          // Côté descendant (haut-droite) - suit la courbure
          const pointsCourbe2 = this.extrairePointsCourbeLosange(indexMilieu, indexFin, pointsSegment, hauteur + hauteurLosange, hauteur);
          const traverse2 = this.creerTraverseCourbe(pointsCourbe2, parametres.epaisseur, parametres.materiau);
          traverses.push({
            id: `traverse_losange_${i}_${j}_2`,
            points: traverse2.points,
            indices: traverse2.indices,
            normales: traverse2.normales,
            uv: traverse2.uv
          });

          // Côté descendant (droite-bas) - suit la courbure
          const pointsCourbe3 = this.extrairePointsCourbeLosange(indexFin, indexMilieu, pointsSegment, hauteur, hauteur - hauteurLosange);
          const traverse3 = this.creerTraverseCourbe(pointsCourbe3, parametres.epaisseur, parametres.materiau);
          traverses.push({
            id: `traverse_losange_${i}_${j}_3`,
            points: traverse3.points,
            indices: traverse3.indices,
            normales: traverse3.normales,
            uv: traverse3.uv
          });

          // Côté montant (bas-gauche) - suit la courbure
          const pointsCourbe4 = this.extrairePointsCourbeLosange(indexMilieu, indexDebut, pointsSegment, hauteur - hauteurLosange, hauteur);
          const traverse4 = this.creerTraverseCourbe(pointsCourbe4, parametres.epaisseur, parametres.materiau);
          traverses.push({
            id: `traverse_losange_${i}_${j}_4`,
            points: traverse4.points,
            indices: traverse4.indices,
            normales: traverse4.normales,
            uv: traverse4.uv
          });
          
          continue; // Passer au losange suivant
        } else {
          // Pour les formes rectangulaires, calculer les points par interpolation linéaire
          pointDebut = {
            x: debutSegment.x + (finSegment.x - debutSegment.x) * debutLosange,
            y: hauteur,
            z: debutSegment.z + (finSegment.z - debutSegment.z) * debutLosange
          };
          
          const pointMilieu = {
            x: debutSegment.x + (finSegment.x - debutSegment.x) * milieuLosange,
            y: hauteur,
            z: debutSegment.z + (finSegment.z - debutSegment.z) * milieuLosange
          };
          
          pointMilieuHaut = {
            x: pointMilieu.x,
            y: hauteur + hauteurLosange,
            z: pointMilieu.z
          };
          
          pointFin = {
            x: debutSegment.x + (finSegment.x - debutSegment.x) * finLosange,
            y: hauteur,
            z: debutSegment.z + (finSegment.z - debutSegment.z) * finLosange
          };
          
          pointMilieuBas = {
            x: pointMilieu.x,
            y: hauteur - hauteurLosange,
            z: pointMilieu.z
          };
        }

        // Créer les 4 côtés du losange
        
        // Côté montant (gauche-haut)
        const traverse1 = this.creerTraverseDroite(pointDebut, pointMilieuHaut, pointDebut.y, parametres.epaisseur, parametres.materiau);
        traverses.push({
          id: `traverse_losange_${i}_${j}_1`,
          points: traverse1.points,
          indices: traverse1.indices,
          normales: traverse1.normales,
          uv: traverse1.uv
        });
        
        // Côté descendant (haut-droite)
        const traverse2 = this.creerTraverseDroite(pointMilieuHaut, pointFin, pointMilieuHaut.y, parametres.epaisseur, parametres.materiau);
        traverses.push({
          id: `traverse_losange_${i}_${j}_2`,
          points: traverse2.points,
          indices: traverse2.indices,
          normales: traverse2.normales,
          uv: traverse2.uv
        });

        // Côté descendant (droite-bas)
        const traverse3 = this.creerTraverseDroite(pointFin, pointMilieuBas, pointFin.y, parametres.epaisseur, parametres.materiau);
        traverses.push({
          id: `traverse_losange_${i}_${j}_3`,
          points: traverse3.points,
          indices: traverse3.indices,
          normales: traverse3.normales,
          uv: traverse3.uv
        });

        // Côté montant (bas-gauche)
        const traverse4 = this.creerTraverseDroite(pointMilieuBas, pointDebut, pointMilieuBas.y, parametres.epaisseur, parametres.materiau);
        traverses.push({
          id: `traverse_losange_${i}_${j}_4`,
          points: traverse4.points,
          indices: traverse4.indices,
          normales: traverse4.normales,
          uv: traverse4.uv
        });
      }
    }

    return traverses;
  }

  /**
   * Génère un motif personnalisé avec répétitions distinctes
   * Tâche 2.5: Outil de Dessin Personnalisé
   */
  private genererMotifPersonnalise(
    hauteur: number,
    parametres: ParametresTraverses,
    donneesBase: DonneesStructureBase
  ): GeometrieTraverse[] {

    
    const traverses: GeometrieTraverse[] = [];
    const positionsMontants = donneesBase.positionsMontants;

    if (!parametres.motifPersonnalise || !positionsMontants || positionsMontants.length < 2) {
      console.warn('🎨 Motif personnalisé non défini ou pas assez de montants, utilisation du motif simple');
      return this.genererMotifSimple(hauteur, parametres, donneesBase);
    }

    const motif = parametres.motifPersonnalise;
    


    // Déterminer le type de forme pour suivre le périmètre comme les traverses vagues
    const estCirculaire = donneesBase.forme === 'cercle';
    const estOvale = donneesBase.forme === 'ovale';
    const estPolygone = donneesBase.forme === 'polygone';
    const estCourbe = estCirculaire || estOvale;
    
    // Calculer le centre et le rayon pour les formes courbes
    const centre = (estCourbe || estPolygone) ? this.calculerCentre(positionsMontants) : null;
    const rayon = estCirculaire && centre ? this.calculerRayon(centre, positionsMontants[0]) : 0;

    // SIMPLE : Prendre votre dessin 2D et le transformer directement en 3D
    // Une traverse par paire de montants, avec votre motif complet
    for (let montantIndex = 0; montantIndex < positionsMontants.length; montantIndex++) {
      const montantActuel = positionsMontants[montantIndex];
      const montantSuivant = positionsMontants[(montantIndex + 1) % positionsMontants.length];
      

      
      // Générer les points de base suivant le périmètre (comme les traverses vagues)
      let pointsBase: Point3D[];
      
      if (estCirculaire && centre && rayon > 0) {
        // Générer les points d'arc pour une structure circulaire
        pointsBase = this.genererPointsArc(montantActuel, montantSuivant, centre, rayon, hauteur);
      } else if (estOvale && centre) {
        // Générer les points d'arc elliptique pour une structure ovale
        pointsBase = this.genererPointsArcElliptique(montantActuel, montantSuivant, centre, donneesBase, hauteur);
      } else if (estPolygone && centre) {
        // Générer les points suivant le périmètre du polygone
        pointsBase = this.genererPointsPolygone(montantActuel, montantSuivant, centre, donneesBase, hauteur);
      } else {
        // Générer des points en ligne droite pour les autres formes
        const nombrePoints = 20;
        pointsBase = [];
        for (let j = 0; j <= nombrePoints; j++) {
          const t = j / nombrePoints;
          pointsBase.push({
            x: montantActuel.x + (montantSuivant.x - montantActuel.x) * t,
            y: hauteur,
            z: montantActuel.z + (montantSuivant.z - montantActuel.z) * t
          });
        }
      }
      
      // Points de début et fin entre les montants (basés sur le périmètre)
      const pointDebut: Point3D = pointsBase[0];
      const pointFin: Point3D = pointsBase[pointsBase.length - 1];
      
      // RÉPÉTER LE MOTIF POUR COUVRIR EXACTEMENT TOUTE LA DISTANCE
      const distanceSegment = Math.sqrt(
        Math.pow(pointFin.x - pointDebut.x, 2) + 
        Math.pow(pointFin.z - pointDebut.z, 2)
      );
      
      // Calculer les VRAIES dimensions du motif dessiné (pas le canvas)
      // Analyser toutes les coordonnées X et Y pour trouver les limites réelles
      const tousLesPointsX: number[] = [];
      const tousLesPointsY: number[] = [];
      
      // Ajouter les points des lignes de crayon
      motif.lignes.forEach(ligne => {
        ligne.forEach(point => {
          tousLesPointsX.push(point.x);
          tousLesPointsY.push(point.y);
        });
      });
      
      // Ajouter les points des lignes droites
      if (motif.lignesDroites) {
        motif.lignesDroites.forEach(ligneDroite => {
          tousLesPointsX.push(ligneDroite.debut.x, ligneDroite.fin.x);
          tousLesPointsY.push(ligneDroite.debut.y, ligneDroite.fin.y);
        });
      }
      
      // Ajouter les points des courbes de Bézier
      if (motif.courbes) {
        motif.courbes.forEach(courbe => {
          tousLesPointsX.push(courbe.debut.x, courbe.fin.x, courbe.controle1.x, courbe.controle2.x);
          tousLesPointsY.push(courbe.debut.y, courbe.fin.y, courbe.controle1.y, courbe.controle2.y);
        });
      }
      
      const minX = Math.min(...tousLesPointsX);
      const maxX = Math.max(...tousLesPointsX);
      const minY = Math.min(...tousLesPointsY);
      const maxY = Math.max(...tousLesPointsY);
      const largeurMotifReellePixels = maxX - minX;
      const hauteurMotifReellePixels = maxY - minY;
      const largeurMotifReelleMetres = largeurMotifReellePixels / 1000; // Conversion pixels -> mètres
      

      
      // MÉTHODE ORIGINALE : Calculer le nombre optimal de répétitions
      const nombreRepetitionsCalcule = Math.max(1, Math.round(distanceSegment / largeurMotifReelleMetres));
      
      // Ajuster la taille pour que les motifs remplissent exactement l'espace
      const tailleMotifAjustee = distanceSegment / nombreRepetitionsCalcule;
      const facteurEchelle = tailleMotifAjustee / largeurMotifReelleMetres;
      
      const nombreRepetitionsOptimal = nombreRepetitionsCalcule;
      
      const nombreRepetitions = nombreRepetitionsOptimal;
      

      
      // Créer EXACTEMENT le nombre de motifs A calculé pour remplir l'espace

        
        // Créer chaque motif individuellement SANS chevauchement
        // La continuité sera assurée par des connexions intelligentes entre motifs
        for (let repetition = 0; repetition < nombreRepetitions; repetition++) {
          // MÉTHODE ORIGINALE: Diviser l'espace total en parts égales
          const progressionDebut = repetition / nombreRepetitions;
          const progressionFin = (repetition + 1) / nombreRepetitions;
          

          
          // Points de début et fin pour ce motif individuel
          const pointDebutMotif: Point3D = {
            x: pointDebut.x + (pointFin.x - pointDebut.x) * progressionDebut,
            y: pointDebut.y,
            z: pointDebut.z + (pointFin.z - pointDebut.z) * progressionDebut
          };
          
          const pointFinMotif: Point3D = {
            x: pointDebut.x + (pointFin.x - pointDebut.x) * progressionFin,
            y: pointFin.y,
            z: pointDebut.z + (pointFin.z - pointDebut.z) * progressionFin
          };
          
          // Créer UNE TRAVERSE PAR LIGNE pour ce motif A individuel
          let ligneIndex = 0;
          
          // Traiter les lignes de crayon
          motif.lignes.forEach((ligne) => {

            
            // Convertir cette ligne en points 3D en suivant le périmètre
            const pointsLigne3D: Point3D[] = [];
            
            // Convertir les points originaux en suivant le périmètre
            const pointsOriginaux: Point3D[] = [];
            ligne.forEach((point2D) => {
              // Calculer la position relative du point dans le motif (0 à 1) - inverser X pour corriger l'effet miroir
              const progressionX = 1 - (point2D.x - minX) / largeurMotifReellePixels;
              const progressionMotif = (repetition + progressionX) / nombreRepetitions;
              
              // Trouver le point correspondant sur le périmètre
              const indexPerimetre = Math.floor(progressionMotif * (pointsBase.length - 1));
              const indexSuivant = Math.min(indexPerimetre + 1, pointsBase.length - 1);
              const t = (progressionMotif * (pointsBase.length - 1)) - indexPerimetre;
              
              // Interpoler entre les points du périmètre
              const pointPerimetre = pointsBase[indexPerimetre];
              const pointSuivant = pointsBase[indexSuivant];
              
              const pointBase3D: Point3D = {
                x: pointPerimetre.x + (pointSuivant.x - pointPerimetre.x) * t,
                y: pointPerimetre.y,
                z: pointPerimetre.z + (pointSuivant.z - pointPerimetre.z) * t
              };
              
              // Appliquer l'offset vertical du motif (inverser pour garder l'orientation correcte)
              const offsetY = (0.5 - (point2D.y - minY) / hauteurMotifReellePixels) * facteurEchelle * 0.2; // Réduire l'amplitude
              
              const point3D: Point3D = {
                x: pointBase3D.x,
                y: pointBase3D.y + offsetY,
                z: pointBase3D.z
              };
              
              pointsOriginaux.push(point3D);
            });
            
            // Interpolation intelligente : limiter le nombre de points intermédiaires
            if (pointsOriginaux.length >= 2) {
              pointsLigne3D.push(pointsOriginaux[0]); // Premier point
              
              // Interpoler entre chaque paire de points avec limitation intelligente
              for (let i = 0; i < pointsOriginaux.length - 1; i++) {
                const pointA = pointsOriginaux[i];
                const pointB = pointsOriginaux[i + 1];
                
                // Calculer la distance entre les points
                const distance = Math.sqrt(
                  Math.pow(pointB.x - pointA.x, 2) + 
                  Math.pow(pointB.y - pointA.y, 2) + 
                  Math.pow(pointB.z - pointA.z, 2)
                );
                
                // Ajouter des points intermédiaires seulement si la distance est significative
                if (distance > 0.1) { // Seuil de 10cm
                  const pointIntermediaire: Point3D = {
                    x: pointA.x + (pointB.x - pointA.x) * 0.5,
                    y: pointA.y + (pointB.y - pointA.y) * 0.5,
                    z: pointA.z + (pointB.z - pointA.z) * 0.5
                  };
                  pointsLigne3D.push(pointIntermediaire);
                }
                
                pointsLigne3D.push(pointB); // Point final
              }
            } else {
              // Si moins de 2 points, utiliser les points originaux
              pointsLigne3D.push(...pointsOriginaux);
            }
            
            // Créer une traverse pour cette ligne de ce motif individuel
            if (pointsLigne3D.length >= 2) {
              const geometrieTraverse = this.creerTraverseCourbe(
                pointsLigne3D,
                parametres.epaisseur,
                parametres.materiau
              );
              
              traverses.push({
                id: `traverse_personnalise_${montantIndex}_motif_${repetition}_ligne_${ligneIndex}`,
                points: geometrieTraverse.points,
                indices: geometrieTraverse.indices,
                normales: geometrieTraverse.normales,
                uv: geometrieTraverse.uv
              });
              

            }
            ligneIndex++;
          });
          
          // Traiter les lignes droites
          if (motif.lignesDroites) {
            motif.lignesDroites.forEach((ligneDroite) => {
  
              
              // Convertir les points de début et fin en 3D
              const pointsOriginaux: Point3D[] = [];
              
              // Point de début
              const progressionXDebut = 1 - (ligneDroite.debut.x - minX) / largeurMotifReellePixels;
              const progressionMotifDebut = (repetition + progressionXDebut) / nombreRepetitions;
              const indexPerimetreDebut = Math.floor(progressionMotifDebut * (pointsBase.length - 1));
              const indexSuivantDebut = Math.min(indexPerimetreDebut + 1, pointsBase.length - 1);
              const tDebut = (progressionMotifDebut * (pointsBase.length - 1)) - indexPerimetreDebut;
              
              const pointPerimetreDebut = pointsBase[indexPerimetreDebut];
              const pointSuivantDebut = pointsBase[indexSuivantDebut];
              
              const pointBase3DDebut: Point3D = {
                x: pointPerimetreDebut.x + (pointSuivantDebut.x - pointPerimetreDebut.x) * tDebut,
                y: pointPerimetreDebut.y,
                z: pointPerimetreDebut.z + (pointSuivantDebut.z - pointPerimetreDebut.z) * tDebut
              };
              
              const offsetYDebut = (0.5 - (ligneDroite.debut.y - minY) / hauteurMotifReellePixels) * facteurEchelle * 0.2;
              
              const point3DDebut: Point3D = {
                x: pointBase3DDebut.x,
                y: pointBase3DDebut.y + offsetYDebut,
                z: pointBase3DDebut.z
              };
              
              // Point de fin
              const progressionXFin = 1 - (ligneDroite.fin.x - minX) / largeurMotifReellePixels;
              const progressionMotifFin = (repetition + progressionXFin) / nombreRepetitions;
              const indexPerimetreFin = Math.floor(progressionMotifFin * (pointsBase.length - 1));
              const indexSuivantFin = Math.min(indexPerimetreFin + 1, pointsBase.length - 1);
              const tFin = (progressionMotifFin * (pointsBase.length - 1)) - indexPerimetreFin;
              
              const pointPerimetreFin = pointsBase[indexPerimetreFin];
              const pointSuivantFin = pointsBase[indexSuivantFin];
              
              const pointBase3DFin: Point3D = {
                x: pointPerimetreFin.x + (pointSuivantFin.x - pointPerimetreFin.x) * tFin,
                y: pointPerimetreFin.y,
                z: pointPerimetreFin.z + (pointSuivantFin.z - pointPerimetreFin.z) * tFin
              };
              
              const offsetYFin = (0.5 - (ligneDroite.fin.y - minY) / hauteurMotifReellePixels) * facteurEchelle * 0.2;
              
              const point3DFin: Point3D = {
                x: pointBase3DFin.x,
                y: pointBase3DFin.y + offsetYFin,
                z: pointBase3DFin.z
              };
              
              pointsOriginaux.push(point3DDebut, point3DFin);
              
              // Créer une traverse droite
              if (pointsOriginaux.length >= 2) {
                const geometrieTraverse = this.creerTraverseCourbe(
                  pointsOriginaux,
                  parametres.epaisseur,
                  parametres.materiau
                );
                
                traverses.push({
                  id: `traverse_personnalise_${montantIndex}_motif_${repetition}_ligne_droite_${ligneIndex}`,
                  points: geometrieTraverse.points,
                  indices: geometrieTraverse.indices,
                  normales: geometrieTraverse.normales,
                  uv: geometrieTraverse.uv
                });
                
    
              }
              ligneIndex++;
            });
          }
          
          // Traiter les courbes de Bézier
          if (motif.courbes) {
            motif.courbes.forEach((courbe) => {
  
              
              // Générer des points le long de la courbe de Bézier
              const nombrePointsCourbe = 20; // Nombre de points pour approximer la courbe
              const pointsOriginaux: Point3D[] = [];
              
              for (let t = 0; t <= 1; t += 1 / (nombrePointsCourbe - 1)) {
                // Calculer le point sur la courbe de Bézier cubique
                const point2D = {
                  x: Math.pow(1 - t, 3) * courbe.debut.x +
                     3 * Math.pow(1 - t, 2) * t * courbe.controle1.x +
                     3 * (1 - t) * Math.pow(t, 2) * courbe.controle2.x +
                     Math.pow(t, 3) * courbe.fin.x,
                  y: Math.pow(1 - t, 3) * courbe.debut.y +
                     3 * Math.pow(1 - t, 2) * t * courbe.controle1.y +
                     3 * (1 - t) * Math.pow(t, 2) * courbe.controle2.y +
                     Math.pow(t, 3) * courbe.fin.y
                };
                
                // Convertir en 3D
                const progressionX = 1 - (point2D.x - minX) / largeurMotifReellePixels;
                const progressionMotif = (repetition + progressionX) / nombreRepetitions;
                const indexPerimetre = Math.floor(progressionMotif * (pointsBase.length - 1));
                const indexSuivant = Math.min(indexPerimetre + 1, pointsBase.length - 1);
                const tInterp = (progressionMotif * (pointsBase.length - 1)) - indexPerimetre;
                
                const pointPerimetre = pointsBase[indexPerimetre];
                const pointSuivant = pointsBase[indexSuivant];
                
                const pointBase3D: Point3D = {
                  x: pointPerimetre.x + (pointSuivant.x - pointPerimetre.x) * tInterp,
                  y: pointPerimetre.y,
                  z: pointPerimetre.z + (pointSuivant.z - pointPerimetre.z) * tInterp
                };
                
                const offsetY = (0.5 - (point2D.y - minY) / hauteurMotifReellePixels) * facteurEchelle * 0.2;
                
                const point3D: Point3D = {
                  x: pointBase3D.x,
                  y: pointBase3D.y + offsetY,
                  z: pointBase3D.z
                };
                
                pointsOriginaux.push(point3D);
              }
              
              // Créer une traverse courbe
              if (pointsOriginaux.length >= 2) {
                const geometrieTraverse = this.creerTraverseCourbe(
                  pointsOriginaux,
                  parametres.epaisseur,
                  parametres.materiau
                );
                
                traverses.push({
                  id: `traverse_personnalise_${montantIndex}_motif_${repetition}_courbe_${ligneIndex}`,
                  points: geometrieTraverse.points,
                  indices: geometrieTraverse.indices,
                  normales: geometrieTraverse.normales,
                  uv: geometrieTraverse.uv
                });
                
    
              }
              ligneIndex++;
            });
          }
        }
        

    }

    
    return traverses;
  }









  

  
  /**
   * Extrait les points de bordure d'un motif selon le côté (début ou fin)
   */

  


  /**
   * Crée une traverse droite simple
   */
  private creerTraverseDroite(
    pointA: Point3D,
    pointB: Point3D,
    hauteur: number,
    epaisseur: number,
    materiau: TypeMateriauTraverse
  ): { points: Point3D[]; indices: number[]; normales: Point3D[]; uv: Point2D[] } {
    if (materiau === TypeMateriauTraverse.TUBE_CARRE) {
      return this.creerTraverseDroiteCarre(pointA, pointB, epaisseur);
    } else {
      return this.creerTraverseDroiteRonde(pointA, pointB, epaisseur);
    }
  }

  /**
   * Crée une traverse droite avec section ronde (fer à béton)
   */
  private creerTraverseDroiteRonde(
    pointA: Point3D,
    pointB: Point3D,
    epaisseur: number
  ): { points: Point3D[]; indices: number[]; normales: Point3D[]; uv: Point2D[] } {
    const rayon = epaisseur / 2;
    const segmentsRadiaux = 8;
    const points: Point3D[] = [];
    const indices: number[] = [];
    const normales: Point3D[] = [];
    const uv: Point2D[] = [];

    // Calculer la direction de la traverse
    const direction = {
      x: pointB.x - pointA.x,
      y: pointB.y - pointA.y,
      z: pointB.z - pointA.z
    };
    const longueur = Math.sqrt(direction.x * direction.x + direction.y * direction.y + direction.z * direction.z);
    
    // Vérifier que les points ne sont pas identiques
    if (longueur < 0.001) {
      return { points: [], indices: [], normales: [], uv: [] };
    }
    
    // Normaliser la direction complète
    direction.x /= longueur;
    direction.y /= longueur;
    direction.z /= longueur;
    
    // Pour la section circulaire, on utilise un vecteur perpendiculaire dans le plan XZ
    const longueurXZ = Math.sqrt(direction.x * direction.x + direction.z * direction.z);
    let perpendiculaire: { x: number; z: number };
    
    if (longueurXZ > 0.001) {
      perpendiculaire = { x: -direction.z / longueurXZ, z: direction.x / longueurXZ };
    } else {
      // Si la traverse est verticale, utiliser un vecteur par défaut
      perpendiculaire = { x: 1, z: 0 };
    }



    // Générer les points du cylindre
    for (let i = 0; i < segmentsRadiaux; i++) {
      const angle = (i / segmentsRadiaux) * Math.PI * 2;
      const cos = Math.cos(angle);
      const sin = Math.sin(angle);
      
      // Calculer les offsets radiaux
      const offsetX = perpendiculaire.x * cos * rayon;
      const offsetZ = perpendiculaire.z * cos * rayon;
      const offsetY = sin * rayon;

      // Point au début de la traverse
      points.push({
        x: pointA.x + offsetX,
        y: pointA.y + offsetY,
        z: pointA.z + offsetZ
      });

      // Point à la fin de la traverse
      points.push({
        x: pointB.x + offsetX,
        y: pointB.y + offsetY,
        z: pointB.z + offsetZ
      });

      // Normales
      normales.push({ x: perpendiculaire.x * cos, y: sin, z: perpendiculaire.z * cos });
      normales.push({ x: perpendiculaire.x * cos, y: sin, z: perpendiculaire.z * cos });

      // Coordonnées UV
      uv.push({ x: i / segmentsRadiaux, y: 0 });
      uv.push({ x: i / segmentsRadiaux, y: 1 });
    }

    // Générer les indices pour les triangles
    for (let i = 0; i < segmentsRadiaux; i++) {
      const base = i * 2;
      const next = ((i + 1) % segmentsRadiaux) * 2;
      
      // Triangle 1
      indices.push(base, base + 1, next);
      // Triangle 2
      indices.push(base + 1, next + 1, next);
    }

    return { points, indices, normales, uv };
  }

  /**
   * Crée une traverse courbe avec section carrée (tube carré)
   */
  private creerTraverseCourbeCarre(
    pointsCourbe: Point3D[],
    epaisseur: number
  ): { points: Point3D[]; indices: number[]; normales: Point3D[]; uv: Point2D[] } {
    const demiEpaisseur = epaisseur / 2;
    const points: Point3D[] = [];
    const indices: number[] = [];
    const normales: Point3D[] = [];
    const uv: Point2D[] = [];

    // Créer des segments carrés entre chaque paire de points de la courbe
    for (let segmentIndex = 0; segmentIndex < pointsCourbe.length - 1; segmentIndex++) {
      const pointA = pointsCourbe[segmentIndex];
      const pointB = pointsCourbe[segmentIndex + 1];
      
      // Utiliser la méthode de section carrée pour chaque segment
      const segment = this.creerTraverseDroiteCarre(pointA, pointB, epaisseur);
      
      // Ajouter les points du segment avec offset d'index
      const offsetIndex = points.length;
      points.push(...segment.points);
      normales.push(...segment.normales);
      uv.push(...segment.uv);
      
      // Ajouter les indices avec offset
      for (const index of segment.indices) {
        indices.push(index + offsetIndex);
      }
    }

    return { points, indices, normales, uv };
  }

  /**
   * Crée une traverse droite avec section carrée (tube carré)
   */
  private creerTraverseDroiteCarre(
    pointA: Point3D,
    pointB: Point3D,
    epaisseur: number
  ): { points: Point3D[]; indices: number[]; normales: Point3D[]; uv: Point2D[] } {
    const demiEpaisseur = epaisseur / 2;
    const points: Point3D[] = [];
    const indices: number[] = [];
    const normales: Point3D[] = [];
    const uv: Point2D[] = [];

    // Calculer la direction de la traverse
    const direction = {
      x: pointB.x - pointA.x,
      y: pointB.y - pointA.y,
      z: pointB.z - pointA.z
    };
    const longueur = Math.sqrt(direction.x * direction.x + direction.y * direction.y + direction.z * direction.z);
    
    // Vérifier que les points ne sont pas identiques
    if (longueur < 0.001) {
      return { points: [], indices: [], normales: [], uv: [] };
    }
    
    // Normaliser la direction complète
    direction.x /= longueur;
    direction.y /= longueur;
    direction.z /= longueur;
    
    // Pour la section carrée, calculer les vecteurs perpendiculaires
    const longueurXZ = Math.sqrt(direction.x * direction.x + direction.z * direction.z);
    let perpendiculaire1: { x: number; z: number };
    let perpendiculaire2: { x: number; z: number };
    
    if (longueurXZ > 0.001) {
      perpendiculaire1 = { x: -direction.z / longueurXZ, z: direction.x / longueurXZ };
      perpendiculaire2 = { x: direction.x / longueurXZ, z: direction.z / longueurXZ };
    } else {
      // Si la traverse est verticale, utiliser des vecteurs par défaut
      perpendiculaire1 = { x: 1, z: 0 };
      perpendiculaire2 = { x: 0, z: 1 };
    }
    
    // Générer les 8 points du tube carré (4 coins × 2 extrémités)
    const coins = [
      { u: -demiEpaisseur, v: -demiEpaisseur },
      { u: demiEpaisseur, v: -demiEpaisseur },
      { u: demiEpaisseur, v: demiEpaisseur },
      { u: -demiEpaisseur, v: demiEpaisseur }
    ];

    // Créer les points pour chaque coin aux deux extrémités
    for (let i = 0; i < coins.length; i++) {
      const coin = coins[i];
      
      // Calculer les offsets pour la section carrée en utilisant les deux vecteurs perpendiculaires
      const offsetX = perpendiculaire1.x * coin.u + perpendiculaire2.x * coin.v;
      const offsetZ = perpendiculaire1.z * coin.u + perpendiculaire2.z * coin.v;
      const offsetY = 0; // Pas d'offset vertical pour la section

      // Point au début de la traverse
      points.push({
        x: pointA.x + offsetX,
        y: pointA.y + offsetY,
        z: pointA.z + offsetZ
      });

      // Point à la fin de la traverse
      points.push({
        x: pointB.x + offsetX,
        y: pointB.y + offsetY,
        z: pointB.z + offsetZ
      });

      // Normales pour les faces (calculées correctement)
      const normale = {
        x: perpendiculaire1.x * Math.sign(coin.u) + perpendiculaire2.x * Math.sign(coin.v),
        y: 0,
        z: perpendiculaire1.z * Math.sign(coin.u) + perpendiculaire2.z * Math.sign(coin.v)
      };
      // Normaliser la normale
      const longueurNormale = Math.sqrt(normale.x * normale.x + normale.y * normale.y + normale.z * normale.z);
      if (longueurNormale > 0) {
        normale.x /= longueurNormale;
        normale.y /= longueurNormale;
        normale.z /= longueurNormale;
      }
      normales.push(normale);
      normales.push(normale);

      // Coordonnées UV
      uv.push({ x: i / 4, y: 0 });
      uv.push({ x: i / 4, y: 1 });
    }

    // Générer les indices pour les faces du tube carré
    for (let i = 0; i < 4; i++) {
      const base = i * 2;
      const next = ((i + 1) % 4) * 2;
      
      // Face externe
      indices.push(base, base + 1, next);
      indices.push(base + 1, next + 1, next);
    }

    return { points, indices, normales, uv };
  }

  /**
   * Crée une traverse qui suit une courbe définie par une série de points
   */
  private creerTraverseCourbe(
    pointsCourbe: Point3D[],
    epaisseur: number,
    materiau: TypeMateriauTraverse
  ): { points: Point3D[]; indices: number[]; normales: Point3D[]; uv: Point2D[] } {
    if (pointsCourbe.length < 2) {
      // Fallback vers une traverse droite simple
      const point = pointsCourbe[0] || { x: 0, y: 0, z: 0 };
      return this.creerTraverseDroite(point, point, point.y, epaisseur, materiau);
    }

    if (materiau === TypeMateriauTraverse.TUBE_CARRE) {
      return this.creerTraverseCourbeCarre(pointsCourbe, epaisseur);
    } else {
      return this.creerTraverseCourbeRonde(pointsCourbe, epaisseur);
    }
  }

  /**
   * Crée une traverse courbe avec section ronde (fer à béton)
   */
  private creerTraverseCourbeRonde(
    pointsCourbe: Point3D[],
    epaisseur: number
  ): { points: Point3D[]; indices: number[]; normales: Point3D[]; uv: Point2D[] } {

    const rayon = epaisseur / 2;
    const segmentsRadiaux = 16; // Augmenté de 8 à 16 pour plus de lissage
    const points: Point3D[] = [];
    const indices: number[] = [];
    const normales: Point3D[] = [];
    const uv: Point2D[] = [];

    // Créer des segments cylindriques entre chaque paire de points de la courbe
    for (let segmentIndex = 0; segmentIndex < pointsCourbe.length - 1; segmentIndex++) {
      const pointA = pointsCourbe[segmentIndex];
      const pointB = pointsCourbe[segmentIndex + 1];
      
      // Calculer la direction du segment
      const direction = {
        x: pointB.x - pointA.x,
        y: pointB.y - pointA.y,
        z: pointB.z - pointA.z
      };
      const longueur = Math.sqrt(direction.x * direction.x + direction.y * direction.y + direction.z * direction.z);
      
      // Vérifier que les points ne sont pas identiques
      if (longueur < 0.001) {
        continue; // Ignorer ce segment
      }
      
      // Normaliser la direction complète
      direction.x /= longueur;
      direction.y /= longueur;
      direction.z /= longueur;

      // Pour la section circulaire, calculer le vecteur perpendiculaire dans le plan XZ
      const longueurXZ = Math.sqrt(direction.x * direction.x + direction.z * direction.z);
      let perpendiculaire: { x: number; z: number };
      
      if (longueurXZ > 0.001) {
        perpendiculaire = { x: -direction.z / longueurXZ, z: direction.x / longueurXZ };
      } else {
        // Si le segment est vertical, utiliser un vecteur par défaut
        perpendiculaire = { x: 1, z: 0 };
      }
      
      const baseIndex = points.length;
      
      // Générer les points du cylindre pour ce segment
      for (let i = 0; i < segmentsRadiaux; i++) {
        const angle = (i / segmentsRadiaux) * Math.PI * 2;
        const cos = Math.cos(angle);
        const sin = Math.sin(angle);
        
        // Calculer les offsets radiaux
        const offsetX = perpendiculaire.x * cos * rayon;
        const offsetZ = perpendiculaire.z * cos * rayon;
        const offsetY = sin * rayon;

        // Point au début du segment
        points.push({
          x: pointA.x + offsetX,
          y: pointA.y + offsetY,
          z: pointA.z + offsetZ
        });

        // Point à la fin du segment
        points.push({
          x: pointB.x + offsetX,
          y: pointB.y + offsetY,
          z: pointB.z + offsetZ
        });

        // Normales
        normales.push({ x: perpendiculaire.x * cos, y: sin, z: perpendiculaire.z * cos });
        normales.push({ x: perpendiculaire.x * cos, y: sin, z: perpendiculaire.z * cos });

        // Coordonnées UV
        uv.push({ x: i / segmentsRadiaux, y: segmentIndex / (pointsCourbe.length - 1) });
        uv.push({ x: i / segmentsRadiaux, y: (segmentIndex + 1) / (pointsCourbe.length - 1) });
      }

      // Générer les indices pour ce segment
      for (let i = 0; i < segmentsRadiaux; i++) {
        const base = baseIndex + i * 2;
        const nextIndex = (i + 1) % segmentsRadiaux;
        const nextBase = baseIndex + nextIndex * 2;
        
        // Triangle 1
        indices.push(base, base + 1, nextBase);
        // Triangle 2
        indices.push(base + 1, nextBase + 1, nextBase);
      }
    }

    return { points, indices, normales, uv };
  }



  /**
   * Convertit un point 2D du motif en point 3D entre deux montants
   */
  private convertirPoint2DTo3DEntreMontants(
    point2D: Point2D,
    pointDebut: Point3D,
    pointFin: Point3D,
    hauteurBase: number,
    largeurMotif: number,
    hauteurMotif: number,
    minX?: number,
    maxX?: number,
    facteurEchelle?: number,
    minY?: number,
    maxY?: number
  ): Point3D {
    // Utiliser les limites réelles du motif si fournies
    const largeurReelle = (minX !== undefined && maxX !== undefined) ? (maxX - minX) : largeurMotif;
    const offsetX = minX || 0;
    const maxXReel = maxX || largeurMotif;
    const echelle = facteurEchelle || 1.0;
    
    // Normaliser par rapport aux limites RÉELLES du dessin SANS facteur d'échelle
    // Chaque motif occupe exactement sa portion d'espace (0 à 1)
    // Inversion de l'axe X pour corriger l'effet miroir et normalisation correcte
    const progressionX = (maxXReel - point2D.x) / largeurReelle;
    
    // Position 3D entre les montants
    const x = pointDebut.x + (pointFin.x - pointDebut.x) * progressionX;
    const z = pointDebut.z + (pointFin.z - pointDebut.z) * progressionX;
    
    // Y du motif 2D -> hauteur 3D INVERSÉE (votre dessin est à l'envers)
    // Utiliser les limites réelles du dessin Y si fournies
    const hauteurReelle = (minY !== undefined && maxY !== undefined) ? (maxY - minY) : hauteurMotif;
    const offsetY = minY || 0;
    const maxYReel = maxY || hauteurMotif;
    
    // Normaliser par rapport aux limites RÉELLES du dessin en Y
    // Inversion de l'axe Y pour corriger l'orientation
    const progressionY = (maxYReel - point2D.y) / hauteurReelle;
    
    // Calculer la hauteur proportionnelle basée sur la largeur du segment
    const distanceSegment = Math.sqrt(
      Math.pow(pointFin.x - pointDebut.x, 2) + 
      Math.pow(pointFin.z - pointDebut.z, 2)
    );
    
    // Ratio hauteur/largeur du motif original
    const ratioHauteurLargeur = hauteurReelle / largeurReelle;
    
    // Hauteur proportionnelle au segment
    const hauteurProportionnelle = distanceSegment * ratioHauteurLargeur * echelle;
    
    // Centrer le motif verticalement par rapport à hauteurBase
    // progressionY va de 0 à 1, on veut centrer autour de hauteurBase
    const offsetCentrage = hauteurProportionnelle / 2;
    const y = hauteurBase - offsetCentrage + (progressionY * hauteurProportionnelle);
    

    
    return { x, y, z };
  }

  /**
   * CORRECTION FINALE - Applique le motif personnalisé avec répétitions PARFAITEMENT VISIBLES
   * Chaque "A" sera clairement répété et séparé
   */
  private appliquerMotifPersonnaliseAuxPoints(
    pointsBase: Point3D[],
    motif: MotifPersonnalise,
    hauteurBase: number
  ): Point3D[] {
    if (!motif.lignes || motif.lignes.length === 0) {

      return pointsBase;
    }



    
    // Points de début et fin de la traverse
    const pointDebut = pointsBase[0];
    const pointFin = pointsBase[pointsBase.length - 1];
    const distanceTraverse = Math.sqrt(
      Math.pow(pointFin.x - pointDebut.x, 2) + 
      Math.pow(pointFin.z - pointDebut.z, 2)
    );



    // Analyser TOUTES les lignes du motif
    const tousLesPointsMotif: Point2D[] = [];
    motif.lignes.forEach((ligne, index) => {

      tousLesPointsMotif.push(...ligne);
    });
    
    if (tousLesPointsMotif.length === 0) {

      return pointsBase;
    }
    

    
    // Limites exactes du motif
    const minX = Math.min(...tousLesPointsMotif.map(p => p.x));
    const maxX = Math.max(...tousLesPointsMotif.map(p => p.x));
    const minY = Math.min(...tousLesPointsMotif.map(p => p.y));
    const maxY = Math.max(...tousLesPointsMotif.map(p => p.y));
    const largeurMotif = maxX - minX;
    const hauteurMotif = maxY - minY;
    


    
    // Paramètres ULTRA SIMPLES pour répétition claire
    const tailleMotif = 0.8; // 80cm par motif
    const espacement = 0.2; // 20cm entre motifs
    const tailleComplete = tailleMotif + espacement;
    const nbRepetitions = Math.max(1, Math.floor(distanceTraverse / tailleComplete));
    const amplitudeY = 0.3; // 30cm d'amplitude
    


    const pointsFinaux: Point3D[] = [];
    
    // Créer chaque répétition SÉPARÉMENT
    for (let rep = 0; rep < nbRepetitions; rep++) {
      const debutRep = rep * tailleComplete;
      const finRep = debutRep + tailleMotif;
      


      
      // Traiter CHAQUE ligne du motif pour cette répétition
      motif.lignes.forEach((ligne, indexLigne) => {

        
        ligne.forEach((point2D, indexPoint) => {
          // Position X normalisée dans le motif (0 à 1)
          const xNorm = largeurMotif > 0 ? (point2D.x - minX) / largeurMotif : 0.5;
          
          // Position réelle sur cette répétition
          const positionX = debutRep + xNorm * tailleMotif;
          
          // Vérifier qu'on reste dans la traverse
          if (positionX <= distanceTraverse) {
            // Position globale sur la traverse (0 à 1)
            const t = positionX / distanceTraverse;
            
            // Coordonnées 3D finales
            const x3D = pointDebut.x + (pointFin.x - pointDebut.x) * t;
            const z3D = pointDebut.z + (pointFin.z - pointDebut.z) * t;
            
            // Hauteur avec variation du motif
            const yNorm = hauteurMotif > 0 ? (point2D.y - minY) / hauteurMotif - 0.5 : 0;
            const y3D = hauteurBase + yNorm * amplitudeY;
            
            pointsFinaux.push({ x: x3D, y: y3D, z: z3D });
            
            if (indexPoint === 0) {

            }
          }
        });
      });
    }





    
    return pointsFinaux;
  }

  private convertirPoint2DTo3D(
    point2D: Point2D,
    pointDebut: Point3D,
    pointFin: Point3D,
    hauteurBase: number,
    largeurTonnelle: number,
    longueurTonnelle: number,
    motifLargeur: number,
    motifHauteur: number
  ): Point3D {
    // Normaliser les coordonnées 2D du motif (0-1)
    const tX = point2D.x / motifLargeur; // x normalisé (0 à 1)
    const tY = point2D.y / motifHauteur; // y normalisé (0 à 1)
    
    // Convertir en coordonnées 3D de la tonnelle
    // x du motif -> z de la tonnelle (longueur)
    // y du motif -> x de la tonnelle (largeur)
    const x = (tY - 0.5) * largeurTonnelle; // Centré sur la largeur
    const z = (tX - 0.5) * longueurTonnelle; // Centré sur la longueur
    const y = hauteurBase;


    return { x, y, z };
  }

  /**
   * Génère des points le long d'une courbe de Bézier
   */
  private genererPointsCourbeBezier(
    courbe: CourbeBezier,
    nombrePoints: number
  ): Point2D[] {
    const points: Point2D[] = [];

    for (let i = 0; i <= nombrePoints; i++) {
      const t = i / nombrePoints;
      const point = this.evaluerCourbeBezier(courbe, t);
      points.push(point);
    }

    return points;
  }

  /**
   * Évalue une courbe de Bézier cubique à un paramètre t donné
   */
  private evaluerCourbeBezier(courbe: CourbeBezier, t: number): Point2D {
    const t2 = t * t;
    const t3 = t2 * t;
    const mt = 1 - t;
    const mt2 = mt * mt;
    const mt3 = mt2 * mt;

    return {
      x: mt3 * courbe.debut.x + 3 * mt2 * t * courbe.controle1.x + 3 * mt * t2 * courbe.controle2.x + t3 * courbe.fin.x,
      y: mt3 * courbe.debut.y + 3 * mt2 * t * courbe.controle1.y + 3 * mt * t2 * courbe.controle2.y + t3 * courbe.fin.y
    };
  }

  /**
   * Crée un mesh Three.js à partir d'une géométrie de traverse
   */
  private creerMeshTraverse(traverse: GeometrieTraverse): THREE.Mesh | null {
    try {
      const geometrie = new THREE.BufferGeometry();
      
      // Convertir les points en Float32Array
      const positions = new Float32Array(traverse.points.length * 3);
      const normales = new Float32Array(traverse.normales.length * 3);
      const uvs = new Float32Array(traverse.uv.length * 2);
      
      for (let i = 0; i < traverse.points.length; i++) {
        positions[i * 3] = traverse.points[i].x;
        positions[i * 3 + 1] = traverse.points[i].y;
        positions[i * 3 + 2] = traverse.points[i].z;
        
        normales[i * 3] = traverse.normales[i].x;
        normales[i * 3 + 1] = traverse.normales[i].y;
        normales[i * 3 + 2] = traverse.normales[i].z;
        
        uvs[i * 2] = traverse.uv[i].x;
        uvs[i * 2 + 1] = traverse.uv[i].y;
      }
      
      geometrie.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      geometrie.setAttribute('normal', new THREE.BufferAttribute(normales, 3));
      geometrie.setAttribute('uv', new THREE.BufferAttribute(uvs, 2));
      geometrie.setIndex(traverse.indices);
      
      const mesh = new THREE.Mesh(geometrie, this.materiau);
      mesh.name = traverse.id;
      return mesh;
    } catch (error) {
      console.error('Erreur création mesh traverse:', error);
      return null;
    }
  }

  /**
   * Crée une structure vide quand les traverses sont désactivées
   */
  private creerStructureVide(parametres: ParametresTraverses): StructureTraverses {
    return {
      id: 'structure_traverses_vide',
      parametres,
      rangees: []
    };
  }

  /**
   * Met à jour le matériau des traverses
   */
  public mettreAJourMateriau(couleur: number, transparence?: number): void {
    this.materiau = new THREE.MeshLambertMaterial({
      color: couleur,
      transparent: transparence !== undefined,
      opacity: transparence || 1.0
    });
  }

  /**
   * Nettoie les ressources
   */
  public nettoyer(): void {
    if (this.materiau) {
      this.materiau.dispose();
    }
  }










  /**
   * Applique les exclusions des portes sur une polyligne (points ordonnés)
   * Retourne une liste de segments (chacun étant une polyligne) conservés après découpe
   */
  private appliquerExclusionsPortesSurPolyline(points: Point3D[], hauteurTraverse: number, donneesBase: DonneesStructureBase): Point3D[][] {
    if (!points || points.length === 0) return [];
    const portes = donneesBase.portes || [];
    if (!portes.length) return [points];

    const n = points.length;
    const exclu: boolean[] = new Array(n).fill(false);

    // Tolérance de proximité en mètres pour considérer qu'une traverse "croise" la porte sur le périmètre (plan XZ)
    // Valeur empirique: 5 cm, suffisante pour créer un vide sans z-fighting
    const tolerance = 0.05;

    for (const porte of portes) {
      // Filtrer par hauteur: on ne découpe que si la traverse se situe dans la plage verticale de la porte
      if (hauteurTraverse < porte.yMin - 1e-4 || hauteurTraverse > porte.yMax + 1e-4) continue;
      const courbe = porte.baseCurve || [];
      if (courbe.length < 2) continue;

      for (let i = 0; i < n; i++) {
        const d = this.distancePointAPolylineXZ(points[i], courbe);
        if (d <= tolerance) {
          exclu[i] = true;
        }
      }
    }

    // Dilatation légère des zones exclues pour garantir une coupure visuelle propre
    const dilate: boolean[] = exclu.slice();
    for (let i = 0; i < n; i++) {
      if (exclu[i]) {
        if (i - 1 >= 0) dilate[i - 1] = true;
        if (i + 1 < n) dilate[i + 1] = true;
      }
    }

    // Construire les segments conservés (zones non exclues)
    const segments: Point3D[][] = [];
    let courant: Point3D[] = [];
    for (let i = 0; i < n; i++) {
      if (!dilate[i]) {
        courant.push(points[i]);
      } else {
        if (courant.length > 1) {
          segments.push(courant);
        }
        courant = [];
      }
    }
    if (courant.length > 1) segments.push(courant);

    // Ajustement des extrémités: faire s'arrêter les segments exactement aux jambages des portes
    if (segments.length && portes.length) {
      const portesApplicables = portes
        .filter(p => !(hauteurTraverse < p.yMin - 1e-4 || hauteurTraverse > p.yMax + 1e-4))
        .map(p => ({
          courbe: p.baseCurve || [],
          jambA: (p.baseCurve && p.baseCurve.length > 0) ? p.baseCurve[0] : undefined,
          jambB: (p.baseCurve && p.baseCurve.length > 0) ? p.baseCurve[p.baseCurve.length - 1] : undefined
        }))
        .filter(p => p.courbe.length >= 2 && p.jambA && p.jambB) as { courbe: Point3D[]; jambA: Point3D; jambB: Point3D }[];

      // Distance max dans le plan XZ pour enclencher l'accroche aux jambages
      const snapDistance = 0.2; // 20 cm
      const distXZ = (a: Point3D, b: Point3D) => {
        const dx = a.x - b.x; const dz = a.z - b.z; return Math.hypot(dx, dz);
      };

      const snapEndpointToJamb = (pt: Point3D): Point3D => {
        let chosen: Point3D | null = null;
        let best = Infinity;
        for (const p of portesApplicables) {
          const dPoly = this.distancePointAPolylineXZ(pt, p.courbe);
          if (dPoly <= snapDistance) {
            const dA = distXZ(pt, p.jambA);
            const dB = distXZ(pt, p.jambB);
            if (dA < best) { best = dA; chosen = p.jambA; }
            if (dB < best) { best = dB; chosen = p.jambB; }
          }
        }
        if (!chosen) return pt;
        return { x: chosen.x, y: hauteurTraverse, z: chosen.z };
      };

      for (let s = 0; s < segments.length; s++) {
        const seg = segments[s];
        if (seg.length < 2) continue;
        seg[0] = snapEndpointToJamb(seg[0]);
        seg[seg.length - 1] = snapEndpointToJamb(seg[seg.length - 1]);
      }
    }

    return segments.length ? segments : [];
  }

  // Distance d'un point à un segment dans le plan XZ
  private distancePointAuSegmentXZ(p: Point3D, a: Point3D, b: Point3D): number {
    const ax = a.x, az = a.z;
    const bx = b.x, bz = b.z;
    const px = p.x, pz = p.z;

    const abx = bx - ax;
    const abz = bz - az;
    const apx = px - ax;
    const apz = pz - az;

    const ab2 = abx * abx + abz * abz;
    if (ab2 === 0) {
      const dx = px - ax;
      const dz = pz - az;
      return Math.sqrt(dx * dx + dz * dz);
    }

    let t = (apx * abx + apz * abz) * 1.0 / ab2;
    t = Math.max(0, Math.min(1, t));
    const qx = ax + t * abx;
    const qz = az + t * abz;
    const dx = px - qx;
    const dz = pz - qz;
    return Math.sqrt(dx * dx + dz * dz);
  }

  // Distance d'un point à une polyligne dans le plan XZ
  private distancePointAPolylineXZ(p: Point3D, poly: Point3D[]): number {
    let dmin = Infinity;
    for (let i = 0; i + 1 < poly.length; i++) {
      const d = this.distancePointAuSegmentXZ(p, poly[i], poly[i + 1]);
      if (d < dmin) dmin = d;
    }
    return dmin;
  }

  /**
   * Alias pour nettoyer() - compatibilité avec l'interface existante
   */
  public dispose(): void {
    this.nettoyer();
  }
}